/**
 * Body-scroll lock for modal sheets.
 *
 * Without this, opening a bottom sheet on iOS WKWebView leaves the background
 * scrollable — tapping outside the sheet scrolls the page underneath, and
 * touch sequences that start on the scrim can eat input commits inside the
 * sheet (the suspected co-conspirator in "save doesn't save" bugs).
 *
 * The technique below — position-fixed + savedY restore — is the only one
 * that survives iOS Safari / WKWebView in 2026. `overscroll-behavior: contain`
 * alone is not honored on iOS (confirmed by caniuse + WebKit bugs).
 *
 * Position-fixed pins the page but does not stop a scroll container: app.css
 * gives html AND body `overflow-x: hidden`, so both compute `overflow-y: auto`,
 * and body (height 100%) is the page's real scroller (measured in Chromium at
 * 390x844: a 400 px touch pan moves body.scrollTop, never
 * document.scrollingElement). So the lock also sets `overflow-y: hidden` (which
 * keeps the scroll position) and `overscroll-behavior: none` on html and body,
 * and puts back the exact prior inline values on the last unlock (lanes
 * share-sheet-dismiss and sheet-swipe, 2026-09-10).
 *
 * Refcounted so nested sheets (sheet-from-sheet) compose without losing the
 * scroll position. lock/unlock pairs MUST balance; an unbalanced unlock is a
 * no-op (decrement clamped at zero).
 */

const PAGE_PROPS = ["overflow-y", "overscroll-behavior"] as const;

let lockCount = 0;
let savedY = 0;
let savedPage: string[][] = [];

export function lockBodyScroll(): void {
  if (lockCount++ > 0) return;
  savedY = window.scrollY;
  const b = document.body;
  b.style.position = "fixed";
  b.style.top = `-${savedY}px`;
  b.style.left = "0";
  b.style.right = "0";
  b.style.width = "100%";
  const page = [document.documentElement, b];
  savedPage = page.map((el) => PAGE_PROPS.map((p) => el.style.getPropertyValue(p)));
  for (const el of page) {
    el.style.setProperty("overflow-y", "hidden");
    el.style.setProperty("overscroll-behavior", "none");
  }
}

export function unlockBodyScroll(): void {
  if (lockCount === 0) return;
  if (--lockCount > 0) return;
  const b = document.body;
  b.style.position = "";
  b.style.top = "";
  b.style.left = "";
  b.style.right = "";
  b.style.width = "";
  [document.documentElement, b].forEach((el, i) => {
    PAGE_PROPS.forEach((p, j) => {
      const prior = savedPage[i]?.[j];
      if (prior) el.style.setProperty(p, prior);
      else el.style.removeProperty(p);
    });
  });
  window.scrollTo(0, savedY);
}

/** Test-only: reset internal state so unit tests don't leak across cases. */
export function __resetBodyScrollLockForTests(): void {
  lockCount = 0;
  savedY = 0;
  savedPage = [];
}
